import { useEffect, useState, type ReactNode } from 'react'
import type { Period } from '../utils/resolution'
import type { ReplayState } from '../core/ReplayController'
import { DrawerSelect } from './drawerControls'
import { formatReplayClock } from './formatReplayClock'
import { REPLAY_SPEED_OPTIONS as SPEED_OPTIONS } from '../utils/replaySpeeds'

/**
 * Narrow controller seam so ReplayControls unit-tests against a fake — mirrors
 * the ReplayController public surface the strip actually drives. ChartManager's
 * real ReplayController satisfies this structurally.
 */
export interface ReplayControllerLike {
  step(): Promise<boolean> | boolean
  play(): void
  pause(): void
  setSpeed(barsPerSec: number): void
  exit(): void
  getState(): ReplayState
}

/** Minimal EventBus seam (subscribe returns an unsubscribe). */
export interface ReplayEventBusLike {
  on(event: string, handler: (payload: unknown) => void): () => void
}

export interface ReplayControlsProps {
  controller: ReplayControllerLike
  eventBus: ReplayEventBusLike
  /** Active period — drives the period-aware replay-clock format. */
  period: Period
  /** Timezone for the replay clock (defaults to exchange tz). */
  timezone?: string
  /**
   * True while the loop-range selection is armed (first press made, the two
   * range clicks not yet placed). Host-tracked from `replay:loop-armed` /
   * `replay:loop-ended` - the controller's own state only knows `looping`.
   */
  loopArming?: boolean
  /** Loop button press (arm selection / cancel / stop loop - the host's
   *  toggle decides). The button renders only when this is provided. */
  onToggleLoop?: () => void
}

/** Speed presets (bars per second). */
const SPEED_SELECT_OPTIONS = SPEED_OPTIONS.map((s) => ({ value: String(s), label: `${s}×` }))

/**
 * Glass chip family: the Toolbar.tsx `chipStyle` idiom, scaled UP for replay.
 * The strip floats over the candles with nothing around it to anchor the eye,
 * so it runs 28px with a heavier fill and brighter glyphs than the 22px toolbar
 * chips: at toolbar weight users reported missing it entirely.
 */
const CHIP_SIZE = 28
const CHIP_BASE: React.CSSProperties = {
  minWidth: CHIP_SIZE,
  height: CHIP_SIZE,
  padding: '2px 8px',
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid hsl(var(--chart-text) / 0.14)',
  background: 'hsl(var(--chart-text) / 0.16)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
  color: '#c5cbd6',
  cursor: 'pointer',
  transition: 'all 0.15s',
}

/** Tailwind twin of CHIP_BASE, since DrawerSelect styles its trigger by className. */
const SPEED_TRIGGER_CLS =
  'flex h-7 min-w-7 items-center gap-1 rounded-md border border-[hsl(var(--chart-text)/0.14)] ' +
  'bg-[hsl(var(--chart-text)/0.16)] px-2 text-xs font-medium text-[#c5cbd6] ' +
  'shadow-[0_2px_8px_rgba(0,0,0,0.45)] backdrop-blur-[8px] transition-colors ' +
  'hover:bg-[hsl(var(--chart-text)/0.28)] hover:text-white'

function Icon({ d, size = 16 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  )
}

function ChipButton({
  title,
  onClick,
  disabled,
  active,
  children,
}: {
  title: string
  onClick: () => void
  disabled?: boolean
  /** Latched-on look (the loop button while armed/looping): accent border + glyph. */
  active?: boolean
  children: ReactNode
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...CHIP_BASE,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        ...(active ? { border: '1px solid rgba(41, 98, 255, 0.7)' } : {}),
        background: active
          ? 'rgba(41, 98, 255, 0.25)'
          : !disabled && hover
            ? 'hsl(var(--chart-text) / 0.28)'
            : 'hsl(var(--chart-text) / 0.16)',
        color: disabled ? '#787b86' : active || hover ? '#ffffff' : '#c5cbd6',
      }}
    >
      {children}
    </button>
  )
}

/**
 * Floating glass strip rendered by EdgeChart while a bar replay is active.
 * Bottom-center, above the x-axis, over the canvas but below modals. Subscribes
 * to the manager's `replay:*` events and re-reads `getState()` so it tracks the
 * engine without polling. Exit is only via the ✕ chip (Escape pauses, it does
 * not exit — see EdgeChart's capture-phase handler).
 *
 * Spec: docs/superpowers/specs/2026-07-07-bar-replay-design.md
 */
export function ReplayControls({
  controller,
  eventBus,
  period,
  timezone,
  loopArming,
  onToggleLoop,
}: ReplayControlsProps) {
  const [state, setState] = useState<ReplayState>(() => controller.getState())

  useEffect(() => {
    const sync = () => setState(controller.getState())
    sync()
    const events = [
      'replay:started',
      'replay:step',
      'replay:played',
      'replay:paused',
      // Speed changes made while PAUSED produce no other event, so without this
      // the controlled <select> reverts until playback starts.
      'replay:speed',
      'replay:exhausted',
      'replay:ended',
      // Loop start/stop flip state.looping (the loop button's latched look).
      'replay:loop-started',
      'replay:loop-ended',
    ]
    const offs = events.map((e) => eventBus.on(e, sync))
    return () => offs.forEach((off) => off())
  }, [controller, eventBus])

  const { playing, cursorTs, speed, exhausted, stepped, looping } = state
  const stepDisabled = exhausted
  const playDisabled = exhausted && !playing

  return (
    <div
      data-testid="replay-controls"
      className="pointer-events-auto absolute bottom-9 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1.5"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ChipButton
        title="Step forward"
        disabled={stepDisabled}
        onClick={() => void controller.step()}
      >
        {/* skip-forward ▸| */}
        <Icon d="M5 4l10 8-10 8V4zM19 5v14" />
      </ChipButton>

      <ChipButton
        title={playing ? 'Pause' : 'Play'}
        disabled={playDisabled}
        onClick={() => (playing ? controller.pause() : controller.play())}
      >
        {playing ? <Icon d="M7 5v14M17 5v14" /> : <Icon d="M6 4l14 8-14 8V4z" />}
      </ChipButton>

      {/* Speed picker (bars/sec). DrawerSelect, not a native <select>: the OS
          menu a native select opens ignores the app's design language, and this
          chip sits on the canvas next to chrome that all shares one look. */}
      <div
        title="Playback speed in bars per second: 1× reveals one bar each second, whatever the timeframe"
        style={{ display: 'flex' }}
      >
        <DrawerSelect
          ariaLabel="Replay speed"
          value={String(speed)}
          options={SPEED_SELECT_OPTIONS}
          onChange={(v) => controller.setSpeed(Number(v))}
          buttonClassName={SPEED_TRIGGER_CLS}
        />
      </div>

      {/* Replay clock */}
      <div
        data-testid="replay-clock"
        style={{
          ...CHIP_BASE,
          cursor: 'default',
          padding: '0 10px',
          fontSize: 12,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {formatReplayClock(cursorTs, period, timezone)}
      </div>

      {/* Exhaustion cue. "Caught up" = the replay genuinely reached the
          present. Exhausted WITHOUT a single step means no data existed ahead
          of the chosen start on any usable resolution — say that instead. */}
      {exhausted && (
        <div
          data-testid="replay-live-edge"
          title={
            stepped
              ? 'Replay caught up to the present — there is no more history to step through. Exit (✕) to return to the live chart.'
              : 'No data found between the chosen start and now on this timeframe. Try a more recent date or a coarser timeframe, then exit (✕).'
          }
          style={{
            ...CHIP_BASE,
            cursor: 'default',
            padding: '0 10px',
            fontSize: 12,
            fontWeight: 600,
            color: stepped ? '#26a69a' : '#f59e0b',
            whiteSpace: 'nowrap',
          }}
        >
          {stepped ? 'Caught up' : 'No data'}
        </div>
      )}

      {/* Loop toggle: arm a two-click range selection, then replay the range
          on repeat (while looping only the start edge + a rightward wash
          show). Second press cancels/stops. */}
      {onToggleLoop && (
        <ChipButton
          title={
            looping
              ? 'Stop looping - the highlight is removed and replay continues normally'
              : loopArming
                ? 'Click two points on the chart to set the loop range (press again or Escape to cancel)'
                : 'Loop replay: click two points on the chart and replay that range on repeat'
          }
          active={loopArming || looping}
          onClick={onToggleLoop}
        >
          {/* repeat ↻ */}
          <Icon d="M17 2l4 4-4 4M3 11v-1a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v1a4 4 0 0 1-4 4H3" />
        </ChipButton>
      )}

      <ChipButton title="Exit replay" onClick={() => controller.exit()}>
        <Icon d="M18 6L6 18M6 6l12 12" />
      </ChipButton>
    </div>
  )
}
